<script lang="ts" setup>
import type { RouterOutput, UpdateOrCreateInstitution } from '~/types'
import { institutionStatusSchema } from '~/server/schemas'

export type InstitutionFormOptions = {
  showInstitutionBaseInputs?: boolean
  showInstitutionMembersInput?: boolean
  institutionRegistrationInputs?: { byEmail?: boolean, byMobilePhone?: boolean }
}

const props = defineProps<{
  payload: UpdateOrCreateInstitution
  options?: InstitutionFormOptions
  isLoading?: boolean
  institutionTypes?: RouterOutput['institutionTypes']['findManyInstitutionType']['data']
}>()
const emit = defineEmits<{ (e: 'save', payload: UpdateOrCreateInstitution): void, (e: 'cancel'): void }>()

const insitutionTypes = computed(() => generateSelectOptions(props.institutionTypes))
const statusOptions = computed(() => generateTranslatedSelectOptions('institution.status', institutionStatusSchema.options))

const { isCityAccount, canUse, canAccessPage, session } = useAuthorization()
const { cloned } = useCloned(props.payload)

const { isDirty } = useDirtyForm(props.payload.data, computed(() => cloned.value.data))
defineExpose({ isDirty })

cloned.value.data.membersToAdd = cloned.value.data.members?.map(member => member.person.id)
const isEmailRequired = computed(() => {
  if (isCityAccount.value) {
    // For city employees, email is not required
    return false
  }
  // For citizens, email required if phoneNumber is not set
  return !cloned.value.data.phoneNumber
})
const isPhoneNumberRequired = computed(() => {
  if (isCityAccount.value) {
    // For city employees, phoneNumber is not required
    return false
  }
  // For citizens, mobilePhone required if email is not set
  return !cloned.value.data.email
})
</script>

<template>
  <div>
    <FormKitForm
      v-model="cloned.data"
      :mode="payload.mode"
      :is-loading="isLoading"
      :full-width-action-buttons="options?.institutionRegistrationInputs ? true : false"
      :custom-save-btn="options?.institutionRegistrationInputs && 'register.asInstitution'"
      @submit="emit('save', cloned)"
      @cancel="$emit('cancel')"
    >
      <AccountRegistrationInstitutionInputs
        v-if="options?.institutionRegistrationInputs"
        :options="options"
      />
      <template v-else>
        <template v-if="options?.showInstitutionBaseInputs !== false">
          <div class="grid md:grid-cols-2 md:gap-4">
            <FormKit
              name="name"
              type="text"
              :label="$t('institution.name.label')"
              :placeholder="$t('institution.name.placeholder')"
              validation="required"
            />
            <FormKitMultiSelect
              name="typeId"
              :label="$t('institution.type.label')"
              :is-multi-select-allowed="false"
              :multi-select-options="insitutionTypes"
            />
          </div>
          <div class="grid md:grid-cols-2 md:gap-4">
            <FormKit
              v-if="payload.mode === 'create' || canUse('account.update.identifier')"
              name="email"
              type="email"
              :label="$t('institution.email.label')"
              :placeholder="$t('institution.email.label')"
              :validation="[isEmailRequired ? ['required'] : [''], ['email']]"
            />
            <FormKitMultiSelect
              v-if="canAccessPage('/institutions')"
              name="status"
              :label="$t('institution.status.label')"
              :multi-select-options="statusOptions"
              :is-multi-select-allowed="false"
              validation="required"
            />
          </div>
          <div class="md:grid grid-flow-col grid-rows-1 md:gap-4">
            <FormKit
              v-if="payload.mode === 'create' || canUse('account.update.identifier')"
              name="phoneNumber"
              type="tel"
              maxlength="20"
              :label="$t('institution.phoneNumber.label')"
              :placeholder="$t('institution.phoneNumber.placeholder')"
              :validation="[isPhoneNumberRequired ? ['required'] : [''], ['mobilePhone'], ['matches', /^\+?[0-9 \-()]+$/]]"
              :validation-messages="{
                mobilePhone: $t('notifications.error.invalidPhoneNumber'),
              }"
            />
            <FormKitAddress
              name="address"
              :label="$t('institution.address.label')"
              :placeholder="$t('institution.address.placeholder')"
              validation="required|address"
              :validation-messages="{ address: $t('address.invalid') }"
            />
          </div>
          <div class="grid md:grid-cols-2 md:gap-4">
            <FormKit
              name="landLinePhone"
              type="tel"
              maxlength="20"
              :label="$t('institution.landLinePhone.label')"
              :placeholder="$t('institution.landLinePhone.placeholder')"
            />
            <FormKit
              name="faxNumber"
              type="tel"
              maxlength="20"
              :label="$t('institution.faxNumber.label')"
              :placeholder="$t('institution.faxNumber.placeholder')"
            />
          </div>
          <n-divider title-placement="left">
            <div class="text-zinc-700 font-bold">
              {{ $t('institution.contact.person.label') }}
            </div>
          </n-divider>
          <div class="grid md:grid-cols-2 md:gap-4">
            <FormKit
              name="contactPersonFirstName"
              type="text"
              :label="$t('institution.contact.person.firstname.label')"
              :placeholder="$t('institution.contact.person.firstname.placeholder')"
            />
            <FormKit
              name="contactPersonLastName"
              type="text"
              :label="$t('institution.contact.person.lastname.label')"
              :placeholder="$t('institution.contact.person.lastname.placeholder')"
            />
          </div>
          <FormKit
            name="contactPersonEmail"
            type="email"
            :label="$t('institution.contact.person.email.label')"
            :placeholder="$t('institution.contact.person.email.placeholder')"
            validation="email"
          />
        </template>
        <template v-if="options?.showInstitutionMembersInput !== false">
          <FormKitPersonSelector
            v-if="canUse('institutions.members.update')"
            name="membersToAdd"
            translation-key="institutionMember"
            :label="$t('institution.members.label')"
            :multiple="true"
          />
          <FormKitPersonCreator
            v-else-if="cloned.mode === 'update' && cloned.data.account.id === session?.user.id"
            name="membersToAdd"
            translation-key="institutionMember"
            :label="$t('institution.members.label')"
            :multiple="true"
          />
        </template>
      </template>
    </FormKitForm>
  </div>
</template>
